import React, { useEffect, useMemo } from "react";
import { Flex, Text } from "flicket-ui";
import { Select, Badge } from "~components";
import { EventQuery, TicketAvailabilityStatus } from "~graphql/sdk";
import { ListItemDetails } from "../actions/item/actions.item.ListItemDetails";
import PriceWithFee from "../common/common.PriceWithFee";
import { getQuantityOptions } from "~lib/helpers/getQuantityOptions";
import { useRouter } from "next/router";

type ListItemProps = {
  ticketType?: EventQuery["event"]["ticketTypes"][number];
  addonType?: any; // TODO: fix type GetReleaseByIdQuery["release"]["releaseEventAddons"][number]["eventAddon"];
  handleChange: (
    id: string,
    quantity: number,
    type: "event" | "membership" | "addon"
  ) => void;

  quantity?: number;
  isAdmin?: boolean;
  showHiddenFees?: boolean;
  ticketAvailabilityStatus?: TicketAvailabilityStatus;
  addonImage?: File;
  minPurchaseQuantity?: number;
  maxPurchaseQuantity?: number;
};

export const ListItem = ({
  handleChange,
  isAdmin = false,
  showHiddenFees = false,
  ticketType = undefined,
  addonType = undefined,
  quantity = undefined,
  ticketAvailabilityStatus = TicketAvailabilityStatus.Available,
  minPurchaseQuantity = undefined,
  maxPurchaseQuantity = undefined,
}: ListItemProps) => {
  const query = useRouter().query;
  const onChange = (value) =>
    handleChange(
      ticketType ? ticketType?.id : addonType?.id,
      value,
      addonType ? "addon" : "event"
    );

  const id = ticketType?.id || addonType?.id;

  useEffect(() => {
    if (query.ticket === id && query.quantity) {
      const id = setTimeout(() => {
        onChange(parseFloat(query.quantity as string));
      }, 2000);
      return () => {
        clearTimeout(id);
      };
    }
  }, []);

  const quantityOptions = useMemo(() => {
    return getQuantityOptions(minPurchaseQuantity, maxPurchaseQuantity);
  }, [minPurchaseQuantity, maxPurchaseQuantity, isAdmin]);

  const isAddon = !!addonType;

  /* @ts-expect-error something about price being added to ticket type*/
  const price = isAddon ? addonType?.price : ticketType?.price;

  const showHighDemandBadge =
    ticketType?.inHighDemand &&
    ticketAvailabilityStatus === TicketAvailabilityStatus.Available;

  return (
    <Flex alignItems="center" justifyContent="space-between" mb={3} id={id}>
      <Flex flexDir="column" mr={1} w="100%">
        <Text
          fontSize={3}
          lineHeight="normal"
          fontWeight="extraBold"
          color="N800"
        >
          {ticketType?.name || addonType?.name}
        </Text>
        <Text
          fontSize={3}
          fontWeight="medium"
          lineHeight="normal"
          color="N600"
          mt="1/4"
        >
          <PriceWithFee
            price={price}
            ticketType={ticketType}
            showFees={showHiddenFees}
          />
        </Text>
        {ticketAvailabilityStatus ===
          TicketAvailabilityStatus.AllocationExhausted && (
          <Text
            fontSize={2}
            fontWeight="medium"
            lineHeight="normal"
            color="N600"
            mt="1/2"
          >
            All tickets are currently in carts. Please try again later.
          </Text>
        )}
        <ListItemDetails
          name={ticketType?.name || addonType?.name}
          description={ticketType?.description || addonType?.description}
          restrictions={ticketType?.restrictions || addonType?.restrictions}
          addonImage={addonType?.addonImage}
          ticketImage={ticketType?.ticketImage}
          ticketType={ticketType || addonType}
        />
      </Flex>
      <Flex alignItems="center">
        {ticketAvailabilityStatus ===
        TicketAvailabilityStatus.AllocationExhausted ? (
          <Text
            fontSize={1}
            lineHeight="normal"
            color="N600"
            minW="98px"
            textAlign="center"
          >
            Allocation exhausted
          </Text>
        ) : ticketAvailabilityStatus === TicketAvailabilityStatus.Soldout ||
          ticketType?.soldOut ? (
          <Text
            fontSize={1}
            lineHeight="normal"
            color="N600"
            minW="98px"
            textAlign="center"
          >
            Sold out
          </Text>
        ) : (
          <>
            {showHighDemandBadge && <Badge>In High demand</Badge>}
            <Text fontSize={1} lineHeight="normal" color="N600" mr={1}>
              Quantity
            </Text>
            <Select
              minW="98px"
              options={quantityOptions}
              value={quantity}
              onChange={onChange}
              small
              useSingleValue
            />
          </>
        )}
      </Flex>
    </Flex>
  );
};
